import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import Modal from '@/components/base/Modal';
import { useSizes } from '@/hooks/useSizes';
import { toggleSize, deleteSize, type Size } from '@/services/sizes';
import SizeModal from './components/SizeModal';

export default function SizesPage() {
  const { data, loading, error, refetch } = useSizes();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editingSize, setEditingSize] = useState<Size | null>(null);
  const [deletingSize, setDeletingSize] = useState<Size | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [togglingId, setTogglingId] = useState('');

  const filtered = data.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
  });

  async function handleToggle(id: string, current: boolean) {
    setTogglingId(id);
    await toggleSize(id, !current);
    setTogglingId('');
    refetch();
  }

  async function handleDelete() {
    if (!deletingSize) return;
    setDeleting(true);
    setDeleteError('');
    const { error } = await deleteSize(deletingSize.id);
    setDeleting(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeletingSize(null);
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Tallas</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} tallas registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nueva Talla
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total de tallas</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Activas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{data.filter((s) => s.isActive).length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Inactivas</p>
            <p className="text-xl font-bold text-foreground-500 mt-1">{data.filter((s) => !s.isActive).length}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nombre o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Talla</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Descripción</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Tipo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Orden</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-32 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando tallas...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-ruler-line text-accent-600"></i>
                          </div>
                          <p className="text-foreground-900 font-medium">{s.name}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-600">{s.description || '—'}</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${s.sizeType === 'basica' ? 'text-amber-700 bg-amber-50' : 'text-accent-700 bg-accent-50'}`}>
                          {s.sizeType === 'basica' ? 'Básica' : 'Premium'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center text-foreground-600">{s.sortOrder}</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${s.isActive ? 'text-emerald-700 bg-emerald-50' : 'text-foreground-500 bg-background-100'}`}>
                          {s.isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setEditingSize(s)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-foreground-500"></i>
                          </button>
                          <button
                            onClick={() => handleToggle(s.id, s.isActive)}
                            disabled={togglingId === s.id}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                            title={s.isActive ? 'Desactivar' : 'Activar'}
                          >
                            <i className={`${s.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-lg`}></i>
                          </button>
                          <button
                            onClick={() => setDeletingSize(s)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line text-red-500"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-ruler-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay tallas registradas.'
                  : 'No se encontraron tallas con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {(showNew || editingSize) && (
        <SizeModal
          size={editingSize}
          onClose={() => {
            setShowNew(false);
            setEditingSize(null);
          }}
          onSaved={refetch}
        />
      )}

      {deletingSize && (
        <Modal
          title="Eliminar talla"
          subtitle={`Estás a punto de eliminar "${deletingSize.name}".`}
          onClose={() => {
            setDeletingSize(null);
            setDeleteError('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setDeletingSize(null);
                  setDeleteError('');
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Eliminando...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-1.5"></i>Eliminar
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {deleteError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{deleteError}</span>
              </div>
            )}
            <p className="text-sm text-foreground-600">
              Esta acción no se puede deshacer. Si la talla está asociada a productos o inventario,
              no podrá eliminarse y deberás desactivarla en su lugar.
            </p>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}